import {
  Component,
  Input,
  SimpleChanges,
  ElementRef
} from '@angular/core';
import * as d3 from 'd3';

@Component({
    selector: 'app-multi-line-chart',
    templateUrl: './multi-line-chart.component.html',
    styleUrls: ['multi-line-chart.component.css'],
    standalone: false
})
export class MultiLineChartComponent  {
  @Input() private data: any[];
  @Input() private maxRange: number;
  @Input() private minrange: number;
  @Input() private label: string;
  private svg: any;
  private margin = { top: 20, right: 20, bottom: 50, left: 50 };
  private width = 680 - this.margin.left - this.margin.right;
  private height = 230 - this.margin.top - this.margin.bottom;
  private x: any;
  private y: any;
  private tooltip: any;
  private c1 = '#0D0C0C';
  private c2 = '#FF612B';
  private c3 = '#007C89';
  private c4 = '#422C88';

  constructor(private container: ElementRef) { }

  
  ngOnChanges(changes: SimpleChanges): void {
    if (changes.data && changes.data.currentValue.length != 0) {
      this.createSvg();
      this.createScales();
      this.drawLines();
      this.addAxes();
      this.addDots();
    }

  }

  private createSvg(): void {
    d3.select('#multiLineChart').select('svg').remove();
    this.svg = d3.select('#multiLineChart').append('svg')
      .attr('preserveAspectRatio', 'xMidYMid meet')
      .attr("viewBox", `0 0 700 230`)
      .append('g')
      .attr('transform', `translate(${this.margin.left},${this.margin.top})`);
  }

  private createScales(): void {
    const parsed = d3.timeParse('%Y-%m-%d');
    const dates = [];
    this.data.forEach(d => {
      d.d = parsed(d.d);
      dates.push(d.d);
    });
    const domain = d3.extent(dates);
    this.x = d3.scaleTime().range([0, this.width]);
    this.y = d3.scaleLinear().range([this.height, 0]);
    const month = domain[0].getMonth() + 1 == 1 ? 12 : domain[0].getMonth() ;
    const year = month==12 ? domain[0].getFullYear() - 1 : domain[0].getFullYear();
    const day = domain[0].getDate();
    const xDomain = [new Date(month+'-'+day+'-'+year), domain[1]];
    this.x.domain(xDomain);
    this.maxRange = this.maxRange==0 ? 10 :this.maxRange;
    this.y.domain([this.minrange, this.maxRange]);
  }

  private drawLines(): void {
    const lineActuals = d3.line()
      .defined((d: any) => d.actuals !== null)
      .x((d: any) => this.x(d.d) )
      .y((d: any) => this.y(d.actuals));
    const lineProjections = d3.line()
      .defined((d: any) => d.projections !== null)
      .x((d: any) => this.x(d.d))
      .y((d: any) => this.y(d.projections)); 
    const lineForecast1 = d3.line()
      .defined((d: any) => d.forecast1 !== null)
      .x((d: any) => this.x(d.d))
      .y((d: any) => this.y(d.forecast1));
    
    const lineForecast2 = d3.line()
      .defined((d: any) => d.forecast2 !== null)
      .x((d: any) => this.x(d.d))
      .y((d: any) => this.y(d.forecast2));

    
    this.svg.append('path')
      .data([this.data])
      .attr('class', 'line-projections')
      .attr("stroke", this.c4) // Set the stroke color
      .attr("stroke-width", 1) // Set the stroke width
      .attr("fill", "none") // Set the fill color
      .attr('d', lineProjections);

    this.svg.append('path')
      .data([this.data])
      .attr('class', 'line-forecast1')
      .attr("stroke", this.c2) // Set the stroke color
      .attr("stroke-width", 1) // Set the stroke width
      .attr("fill", "none") // Set the fill color
      .attr('d', lineForecast1);

    this.svg.append('path')
      .data([this.data])
      .attr('class', 'line-forecast2')
      .attr("stroke", this.c3) // Set the stroke color
      .attr("stroke-width", 1) // Set the stroke width
      .attr("fill", "none") // Set the fill color
      .attr('d', lineForecast2);

      
      
      this.svg.append('path')
      .data([this.data])
      .attr('class', 'line-actuals')
      .attr("stroke", this.c1) // Set the stroke color
      .attr("stroke-width", 1) // Set the stroke width
      .attr("fill", "none") // Set the fill color
      .attr('d', lineActuals);
  }

  private addDots(): void {
    d3.selectAll('.stacktooltip').remove();
    this.tooltip = d3
      .select(this.container.nativeElement)
      .select('#multiLineChart')
      .append('div')
      .style('opacity', 0)
      .attr('font-weight', 'bold')
      .attr('class', 'stacktooltip')
      .style('border-radius', '8px 8px 8px 8px')
      .style('padding', '5px')
      .style('font-size', '12px')
      .style('width', ' fit-content')
      .style('text-align', 'center')
      .style('text-transform', 'capitalize')
      .style('background', '#000000')
      .style('color', '#fff')
      .style('position', 'absolute')
      .style('z-index', '9999');

    const addDots = (data: any, lineClass: string, valueKey: string) => {
      let color = this.c1;
      if (lineClass == 'forecast1') {
        color = this.c2;
      } else if (lineClass == 'forecast2') {
        color = this.c3;
      }else if (lineClass == 'projections') {
        color = this.c4;
      }
      this.svg.selectAll(`.dot.${lineClass}`)
        .data(data.filter((d: any) => d[valueKey] !== null))
        .enter().append('circle')
        .attr('class', `dot ${lineClass}`)
        .attr('cx', (d: any) => this.x(d.d))
        .attr('cy', (d: any) => this.y(d[valueKey]))
        .attr('r', 2)
        .attr('fill', color)
        .style('position', 'absolute')
        .style('z-index', '9999')
        .on('mouseover', (event, d: any) => {
          const val = event[lineClass];
          const year = event.d.getFullYear();
          const month = event.d.toLocaleString('default', { month: 'short' });
          this.tooltip.transition()
            .duration(200)
            .style('opacity', .9);
          this.tooltip.html(
            ' Year: <b>' +
            year +
            '</b><br>' +
            'Month: <b>' +
            month +
            '</b><br>' +
            this.label +
            ': <b>' +
            val.toFixed(0) +
            '</b>'
          )
          .style("background",color)
            .style("left", (d3.event.pageX ) + "px") 
            .style("top", (d3.event.pageY -60) + "px")

        })
        .on('mouseout', () => {
          this.tooltip.transition()
            .duration(500)
            .style('opacity', 0);
        });
    };

    addDots(this.data, 'projections', 'projections');
    addDots(this.data, 'forecast1', 'forecast1');
    addDots(this.data, 'forecast2', 'forecast2');
    addDots(this.data, 'actuals', 'actuals');
  }

  private addAxes(): void {
       const realStart = d3.min(this.data, (d: any) => d.d); // excludes the padded origin month
    const xAxis = d3.axisBottom(this.x).tickSize(0).tickPadding(0).tickFormat(d3.timeFormat('%Y-%m'))
      .tickValues(d3.timeMonth.range(realStart, d3.timeMonth.offset(this.x.domain()[1], 1), 1)) // offset keeps the last month's tick
    this.svg.append('g')
      .attr('class', 'x-axis')
      .attr('transform', `translate(0,${this.height})`)
      .call(xAxis)
      .selectAll('text')
      .attr('transform', 'rotate(-90)')
      .attr('transform-origin', '4px 7px')
      .style('text-anchor', 'end');

    this.svg.append('g')
      .attr('class', 'y-axis')
      .call(d3.axisLeft(this.y).tickSize(3));
  }
}